/**
 * RouteAnnouncer — sets `document.title` and announces the new page
 * title via a visually-hidden live region for screen-reader users.
 *
 * Mount once inside the AppShell (above the Outlet) with a map of
 * pathname → human title. The map can be sourced from i18n; the
 * component itself does not depend on the i18n plumbing.
 */
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

export interface RouteTitleMap {
  [pathname: string]: string;
}

export function RouteAnnouncer({ titles, baseTitle = 'Nexsoss Operator Console' }: { titles: RouteTitleMap; baseTitle?: string }) {
  const { pathname } = useLocation();
  const [announcement, setAnnouncement] = useState('');

  useEffect(() => {
    const title = titles[pathname] ?? titles['*'] ?? pathname;
    document.title = `${title} — ${baseTitle}`;
    // Defer the announcement to the next tick so screen readers pick
    // up the change even when navigating rapidly between routes.
    const handle = window.setTimeout(() => setAnnouncement(title), 100);
    return () => window.clearTimeout(handle);
  }, [pathname, titles, baseTitle]);

  return (
    <div
      className="nx-sr-only"
      role="status"
      aria-live="polite"
      aria-atomic="true"
      data-testid="route-announcer"
    >
      {announcement}
    </div>
  );
}
